import { create } from 'zustand';
import {
  getCurrentStore,
  getStoreByStoreId,
  getSession,
  getStoreSettings,
} from '../chat-view/services/chat-api.service';
import type {
  Session,
  Store,
  StoreSettings,
} from '../chat-view/services/types';

interface FetchOptions {
  isPlayground: boolean;
}

export interface StoreInfoState {
  store: Store | undefined;
  currentSession: Session | undefined;
  storeSettings: StoreSettings | undefined;
  sessionSource: 'playground' | 'live';
  isLoading: boolean;
  error: unknown;
  /** Prevents concurrent duplicate fetches */
  isFetching: boolean;
  /** Tracks the hostname used for the last fetch to allow re-fetching on change */
  lastFetchedHostname: string | undefined;

  fetchStoreInfo: (hostname: string, options: FetchOptions) => Promise<void>;
  /** Re-fetch store, session and settings; refreshSession refreshes only the session */
  refresh: () => Promise<void>;
  refreshSession: () => Promise<Session | undefined>;
  setStoreSettings: (settings: StoreSettings) => void;
  reset: () => void;
}

const initialState = {
  store: undefined,
  currentSession: undefined,
  storeSettings: undefined,
  sessionSource: 'live' as const,
  isLoading: true,
  error: undefined,
  isFetching: false,
  lastFetchedHostname: undefined,
};

let activeRefreshPromise: Promise<Session | undefined> | null = null;

export const useStoreInfoStore = create<StoreInfoState>()((set, get) => ({
  ...initialState,

  fetchStoreInfo: async (hostname: string, options: FetchOptions) => {
    const state = get();

    // Guard: skip if already fetching or already fetched for this hostname
    if (
      state.isFetching ||
      (state.store && state.lastFetchedHostname === hostname)
    ) {
      return;
    }

    const sessionSource = options.isPlayground ? 'playground' : 'live';
    set({
      isFetching: true,
      isLoading: true,
      error: undefined,
      sessionSource,
    });

    try {
      const configStoreId = (
        window as Window & { superbarConfig?: { storeId?: string } }
      ).superbarConfig?.storeId;
      const store = configStoreId
        ? await getStoreByStoreId(configStoreId)
        : await getCurrentStore(hostname);

      if (!store) {
        set({
          store: undefined,
          isFetching: false,
          isLoading: false,
          lastFetchedHostname: hostname,
        });
        return;
      }

      const [sessionResult, settingsResult] = await Promise.allSettled([
        getSession({
          storeId: store.id,
          source: options.isPlayground ? 'playground' : 'live',
        }),
        getStoreSettings(store.id),
      ]);

      if (sessionResult.status === 'rejected') {
        console.warn('Failed to fetch session:', sessionResult.reason);
      }
      if (settingsResult.status === 'rejected') {
        console.warn('Failed to fetch store settings:', settingsResult.reason);
      }

      // Single atomic update — consumers never see partial state
      set({
        store,
        currentSession:
          sessionResult.status === 'fulfilled'
            ? sessionResult.value
            : undefined,
        storeSettings:
          settingsResult.status === 'fulfilled'
            ? settingsResult.value
            : undefined,
        error:
          sessionResult.status === 'rejected'
            ? sessionResult.reason
            : undefined,
        isFetching: false,
        isLoading: false,
        lastFetchedHostname: hostname,
      });
    } catch (err) {
      set({
        error: err,
        isFetching: false,
        isLoading: false,
        lastFetchedHostname: hostname,
      });
    }
  },

  refresh: async () => {
    const { lastFetchedHostname, sessionSource } = get();
    if (!lastFetchedHostname) return;
    // Invalidate the same-hostname guard so the re-fetch isn't skipped.
    set({ lastFetchedHostname: undefined });
    await get().fetchStoreInfo(lastFetchedHostname, {
      isPlayground: sessionSource === 'playground',
    });
  },

  refreshSession: async () => {
    if (activeRefreshPromise) return activeRefreshPromise;

    activeRefreshPromise = (async () => {
      const { store, sessionSource } = get();
      if (!store?.id) return undefined;
      try {
        const session = await getSession({
          storeId: store.id,
          source: sessionSource,
        });
        set({ currentSession: session });
        return session;
      } catch (err) {
        console.warn('Failed to refresh session:', err);
        return undefined;
      }
    })();

    try {
      return await activeRefreshPromise;
    } finally {
      activeRefreshPromise = null;
    }
  },

  setStoreSettings: (settings: StoreSettings) =>
    set({ storeSettings: settings }),

  reset: () => set(initialState),
}));
